"use client";

import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { WINDOW_ERR_GIVE_IN_TIME, WINDOW_WARN_FAVORIT } from "~/src/const/ui/window/window";
import { createFavorit, deleteFavorit, INVARIT_FAVORIT_COUNT } from "~/src/lib/firestore/favorit";
import { setUserRealtimeListerner } from "~/src/lib/firestore/user";
import {
  addFirstFavoritList,
  existFirstFavoritList,
  removeFirstFavoritList,
} from "~/src/lib/ui/storage";
import { useAuthContext, useAuthSetContext } from "~/src/stores/context/auth/AuthContext";
import { AnalysisDocument, UserDocument } from "~/src/type/firebase/firestore";

export type FavoritProps = {
  isFavorit: boolean;
  analysisId: string;
  contents: AnalysisDocument;
  disable?: boolean;
};

/**
 * いいねアイコン
 *
 * @note ユーザーいいねリスト：ユーザー情報の一部として、firestoreに保存している
 *       初回いいね記事リスト：ユーザーが記事に対して初めていいねを付与した人であるとき、セッションストレージに保存する
 */
export default function Favorit({
  isFavorit,
  analysisId,
  contents,
  disable = false,
}: FavoritProps) {
  const { user } = useAuthContext();
  const { setInfo } = useAuthSetContext();
  const [liked, setLiked] = useState(false);
  const [favoritCount, setFavoritCount] = useState(contents.favoritCount);
  const [isProcessing, setIsProcessing] = useState(false);

  console.log("Favorit 先頭 contents=", contents);
  // console.log("先頭 info?.favoritList=", info?.favoritList);

  console.log(contents?.title, " contents.favoritCount=", contents?.favoritCount);

  /**
   * 初期処理
   */
  useEffect(() => {
    //------------
    // いいね状態の設定
    //------------
    if (user) {
      // ログイン中
      setLiked(isFavorit);
    } else {
      // ログアウトなら、強制でアイコンなし
      setLiked(false);
    }

    //------------
    // 初回いいね直後のいいね数不整合解消
    //------------
    resolutionFirstFavoritCountInconsistency();
  }, [isFavorit, user]); // eslint-disable-line

  // console.log("count=", count);

  /**
   * いいね操作
   */
  const toggleLiked = (event: React.MouseEvent<SVGSVGElement>) => {
    //------------
    // いいね操作不可の場合
    //------------
    if (disable) {
      return;
    }

    //------------
    // イベント伝播停止
    //------------
    event.preventDefault();
    event.stopPropagation();

    //------------
    // 操作中
    //------------
    // 処理なし
    if (isProcessing) return;

    //------------
    // 非操作中
    //------------
    // 操作中に設定
    setIsProcessing(true);

    if (!user) {
      //------------
      // 未ログイン
      //------------
      window.alert(WINDOW_WARN_FAVORIT);
    } else {
      //------------
      // ログイン中
      //------------
      changeLikeState();
    }

    //------------
    // 一定時間経過後、操作可能にする
    //------------
    setTimeout(() => setIsProcessing(false), 1500);
  };

  /**
   * いいね状態の変更
   */
  const changeLikeState = () => {
    try {
      // 変更後のいいね状態
      const afterLikeState = !liked;

      //------------
      // いいね操作
      //------------
      let add: number;
      if (afterLikeState) {
        // いいねする
        createFavorit(user!, analysisId, contents);

        add = 1;
      } else {
        // いいねをはずす
        deleteFavorit(user!, analysisId);

        add = -1;
      }

      //------------
      // UI更新
      //------------
      // アイコン
      setLiked(afterLikeState);

      // （クライアント上の）いいね数
      if (favoritCount !== INVARIT_FAVORIT_COUNT) {
        setFavoritCount((preCount) => preCount! + add);
      }

      //------------
      // ローカル保持データ反映
      //------------
      // ユーザーいいねリストをコンテキストに反映
      updateLocaluserInfo();

      // 初回いいね記事リストをセッションストレージに反映
      setSessionFirstFavorit(afterLikeState);
    } catch (error) {
      //------------
      // エラー
      //------------
      // メッセージを表示
      window.alert(WINDOW_ERR_GIVE_IN_TIME);

      console.log("いいね操作エラー error=", error);
    }
  };

  /**
   * ユーザーいいねリストのローカル反映
   */
  const updateLocaluserInfo = () => {
    //------------
    // リアルタイムリスナー登録
    //------------
    // firestoreの変更を監視し、変更されたときコンテキスト側も更新する
    setUserRealtimeListerner(user!.uid, (userInfo: UserDocument) => {
      //------------
      // ユーザーのいいねリストを最新状態に更新
      //------------
      console.log("リアルタイム検出 favoritList=", userInfo.favoritList);

      setInfo((pre) => {
        return {
          ...pre!,
          favoritList: userInfo.favoritList,
        };
      });
    });
  };

  /**
   * セッションストレージへの初回いいね記事ID更新
   *
   * @note この処理は、ユーザーの操作によっていいね数が「0 → 1」に変化する時のみ実施する。
   *       分析記事の詳細画面へ遷移したとき、いいね数が0のままになる状態を解消することを目的としている。
   *       0のままとなる原因は、functionsによって更新されるまでにラグが生まれるためである。
   *       いいね押下後すぐに分析詳細画面に遷移すると、状況によってはラグによりいいね数が反映されないことがある。
   *       いいねが既にされている状態ならこのラグは問題ない（画面遷移前と値が異なっていても、他ユーザーの影響も考えられるため）が、
   *       「0 → 1」に変化する（ユーザーが一人目のいいね者）場合だと、
   *       画面遷移した後いいね数が0のままなのは違和感があるため、対応する。
   */
  const setSessionFirstFavorit = (afterLikeState: boolean) => {
    //------------
    // セッションストレージの初回いいね記事リストへ反映
    //------------
    if (afterLikeState) {
      //------------
      // 対象外判定
      //------------
      // 記事に対して初回いいね者ではない
      if (favoritCount > 0) return;

      // 追加
      addFirstFavoritList(analysisId);
    } else {
      // 削除
      removeFirstFavoritList(analysisId);
    }
  };

  /**
   * 初回いいね直後のいいね数不整合解消
   *
   * @note ユーザーが初回いいねしたはずなのに、いいね数が0となっている（functionsの処理が遅れている）場合、
   *       いいね数を1に設定する。
   */
  const resolutionFirstFavoritCountInconsistency = () => {
    //------------
    // 対象外判定
    //------------
    // いいねしていない or いいね数が0より大きい値 or いいね数未設定 なら、整合性をあわせる必要なし
    if (!isFavorit || contents.favoritCount > 0 || contents.favoritCount === undefined) return;

    //------------
    // 初回いいね記事リスト
    //------------
    // ユーザーの初回いいね記事リストに、記事IDがあるか
    if (existFirstFavoritList(analysisId)) {
      // いいね数を1にする
      setFavoritCount(1);
    }
  };

  console.log("user?.uid=", user?.uid);

  return (
    <div className="inline-flex items-center">
      {liked ? (
        <HeartIconSolid
          aria-hidden="true"
          className="size-6 text-RED_1"
          onClick={toggleLiked}
        />
      ) : (
        <HeartIconOutline
          aria-hidden="true"
          className="size-6 text-RED_1"
          onClick={toggleLiked}
        />
      )}
      {favoritCount !== INVARIT_FAVORIT_COUNT && (
        <p className="ml-1 text-sm text-GRAY_2 ">{favoritCount}</p>
      )}
    </div>
  );
}
